// /az/careers/$sphere/$career — career detail page (AZ). 404 unless the career
// has a content file AND belongs to $sphere.
import { createFileRoute, notFound } from "@tanstack/react-router";
import { CareerPage } from "@/careers/CareerPage";
import { careersHead, BASE_URL } from "@/careers/head";
import { UI, SPHERE_SLUGS } from "@/careers/spheres";
import { buildCareerPage, careerPageTitle, careerJsonLd } from "@/careers/careerPages";
import { getSubjectPages } from "@/lib/map-public.functions";

const LANG = "az" as const;
const paths = (s: string, c: string) => ({
  az: `/az/careers/${s}/${c}`,
  ru: `/careers/${s}/${c}`,
  en: `/en/careers/${s}/${c}`,
});

export const Route = createFileRoute("/az/careers/$sphere_/$career")({
  loader: ({ params }) => {
    const { sphere, career } = params;
    if (!SPHERE_SLUGS.includes(sphere)) throw notFound();
    const data = buildCareerPage(career, LANG);
    if (!data || data.sphereSlug !== sphere) throw notFound();
    // Страницы предметов, которые есть (≥ 3 анкет): ссылки «нужные предметы» только на них (29.10.2026).
    return getSubjectPages()
      .then((r) => ({ pageCodes: r.pages.map((x) => x.code) }))
      .catch(() => ({ pageCodes: [] as string[] }));
  },
  head: ({ params }) => {
    const { sphere, career } = params;
    const data = buildCareerPage(career, LANG);
    if (!data || data.sphereSlug !== sphere) return {};
    const alt = paths(sphere, career);
    const title = careerPageTitle(data.name, LANG);
    return careersHead({
      lang: LANG,
      canonicalPath: alt[LANG],
      altPaths: alt,
      title,
      description: data.lead,
      jsonLd: careerJsonLd({
        name: data.name,
        description: data.lead,
        sphereName: data.sphereName,
        baseUrl: BASE_URL,
        rootPath: `/az/careers`,
        spherePath: `/az/careers/${sphere}`,
        careerPath: alt[LANG],
        breadcrumbRoot: UI[LANG].breadcrumbRoot,
        pathSteps: data.pathSteps,
      }),
    });
  },
  component: () => {
    const { sphere, career } = Route.useParams();
    const { pageCodes } = Route.useLoaderData();
    return <CareerPage lang={LANG} sphere={sphere} career={career} subjectPages={pageCodes} />;
  },
});
